import { _deepCopy, _numberEnumKey, _numberEnumValues, _omit } from '@naturalcycles/js-lib'
import {
  BirthControlMethodBreakpointOption,
  DeviceTraitOption,
  EligibleAppleWatchBreakpointOption,
  Goal,
  GoalBreakpointOption,
  HormonalBirthControlHowLongBreakpointOption,
  LANG,
  MedicalConditionsBreakpointOption,
  PeriodRegularityOption,
  ProductKey,
  PurchaseNewWatchOption,
  QuizDataInput,
  QuizDataKey,
  QuizFlow,
  SleepConsistencyOption,
  SleepWithTrackerOption,
  TroubleGettingBackToSleepOption,
  WearWatchToBedOption,
  WhichWearableBreakpointOption,
} from '@naturalcycles/shared'
import { medicalConditionsClearblue } from '@src/app/constants/breakpoints/medical-conditions-clearblue.js'
import {
  allQuizSections,
  cBguardQuizData,
  clearblueQuizFlow,
  commonQuizFlow,
  cycleChangesStoreToOptions,
  FertilityGoalInfoPageContent,
} from '@src/app/constants/quiz.js'
import {
  DeviceInfo,
  KeyPoint,
} from '@src/app/pages/quiz/result-overview/result-overview.page.component.js'
import { AnyPage, ClearbluePage, Page, ParentPage } from '@src/app/shared/typings/enum/pages.js'
import { Breakpoint, DefaultSections, Sections } from '@src/app/shared/typings/enum/quiz.js'
import {
  BreakpointOption,
  FlowDefinition,
  QuizSelectOption,
  SignupQuizFlow,
  WSQuizEngineConfig,
} from '@src/app/shared/typings/interfaces/quiz.js'
import { QuizService, QuizSignatureObject } from '../services/quiz/quiz.service.js'

export function clearFlowAfterCurrentSection(
  flow: QuizFlow<Page>,
  currentSection: number,
): QuizFlow<Page> {
  const updated = [...flow]
  for (let i = currentSection + 1; i < updated.length; i++) {
    updated[i] = []
  }
  return updated
}

export abstract class QuizModifier<QUIZ_ENGINE_CONFIG> {
  abstract _shouldEnhance(obj: QuizSignatureObject): boolean
  abstract enhanceEngineConfig(
    obj: QuizSignatureObject,
    config?: QUIZ_ENGINE_CONFIG,
  ): QUIZ_ENGINE_CONFIG
  abstract enhanceSections(obj: QuizSignatureObject, sections?: Sections): Sections
  abstract enhanceInitialFlow(obj: QuizSignatureObject, flow?: SignupQuizFlow): SignupQuizFlow
  abstract enhanceEntry(obj: QuizSignatureObject, entry?: AnyPage[] | null): AnyPage[] | null
  abstract enhanceExit(obj: QuizSignatureObject, exit?: AnyPage[] | null): AnyPage[] | null
}

class MetaLandingPageQuiz implements QuizModifier<WSQuizEngineConfig> {
  public _shouldEnhance(obj: QuizSignatureObject): boolean {
    return obj.isMetaLandingPageFlow
  }

  public enhanceEngineConfig(
    obj: QuizSignatureObject,
    config: WSQuizEngineConfig,
  ): WSQuizEngineConfig {
    if (!this._shouldEnhance(obj)) return config
    config.guardData[Page.name] = () => true
    config.guardData[Page.fertilityGoal] = data => !!data?.name
    return config
  }

  public enhanceSections(obj: QuizSignatureObject, sections: Sections): Sections {
    if (!this._shouldEnhance(obj)) return sections
    return sections
  }

  public enhanceEntry(obj: QuizSignatureObject, entry: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return entry
    return entry
  }

  public enhanceExit(obj: QuizSignatureObject, exit: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return exit
    return exit
  }

  public enhanceInitialFlow(obj: QuizSignatureObject, flow: SignupQuizFlow): SignupQuizFlow {
    if (!this._shouldEnhance(obj)) return flow
    const withoutName = filterPagesFromSection(flow, [Page.name], DefaultSections.intro)
    const withNameAtStart = addPageAtStartOfSection(withoutName, Page.name, DefaultSections.intro)
    return withNameAtStart
  }
}

/**
 * Moves the name question to the start of the quiz
 */
export const metaLandingPageQuiz = new MetaLandingPageQuiz()

class ClearblueQuiz implements QuizModifier<WSQuizEngineConfig> {
  public _shouldEnhance(obj: QuizSignatureObject): boolean {
    return obj.isClearblueFlow
  }

  public enhanceEngineConfig(
    obj: QuizSignatureObject,
    config: WSQuizEngineConfig,
  ): WSQuizEngineConfig {
    if (!this._shouldEnhance(obj)) return config
    // changes medicalConditions breakpoint to clearblue version
    // we can leave the breakpoints later in the flow as is, since they come later they shouldn't interfere
    config.breakpointActions[Breakpoint.medicalConditions] = medicalConditionsClearblue
    config.guardData = cBguardQuizData
    return config
  }

  public enhanceSections(obj: QuizSignatureObject, sections: Sections): Sections {
    if (!this._shouldEnhance(obj)) return sections
    const s = _deepCopy(sections)
    return _omit(s, [DefaultSections.routine])
  }

  public enhanceInitialFlow(obj: QuizSignatureObject, flow: SignupQuizFlow): SignupQuizFlow {
    if (!this._shouldEnhance(obj)) return flow
    return clearblueQuizFlow
  }

  public enhanceEntry(obj: QuizSignatureObject, entry: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return entry
    return [ParentPage.clearblue, ClearbluePage.success]
  }

  public enhanceExit(obj: QuizSignatureObject, exit: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return exit
    return [ParentPage.clearblue, ClearbluePage.offer]
  }
}

export const clearblueQuiz = new ClearblueQuiz()

function fertilityGoalInfoContentData(goal: Goal): FertilityGoalInfoPageContent {
  if (goal === Goal.PLAN) {
    return {
      titleKey: 'txt-plan-pregnancy-guessing',
      buttonKey: 'get-pregnant-faster',
      backgroundColor: 'purple',
      buttonColor: 'shimmer-gradient',
      buttonTextColor: 'black',
      backButtonColor: 'white',
      logoColor: 'white',
    }
  }
  return {
    titleKey: 'txt-effective-typical-use',
    buttonKey: 'txt-next',
    backgroundColor: 'white',
    buttonColor: 'purple',
    buttonTextColor: 'white',
    backButtonColor: '#5f5f5f',
    logoColor: 'purple',
    referenceKey: 'txt-quiz-effectiveness-reference',
  }
}

export function fertilityGoalInfoContent(goal: Goal, locale: LANG): FertilityGoalInfoPageContent {
  const lang = locale.split('-')[0] || 'en'
  const goalKey = _numberEnumKey(Goal, goal)
  const ext = goal === Goal.PLAN ? 'png' : 'svg'
  return {
    ...fertilityGoalInfoContentData(goal),
    imageUrl: `assets/img/fertility-goal-info-${goalKey.toLowerCase()}-${lang}.${ext}`,
  }
}

export function getOptionsForQuestion(storeKey: QuizDataKey): QuizSelectOption[] {
  const options = cycleChangesStoreToOptions[storeKey]
  if (!options) {
    return []
  }

  const { name, en } = options

  return _numberEnumValues(en).map((value: number | string) => {
    return {
      key: value as number,
      textKey: `txt-${name}--${en[value]}`,
    }
  })
}

// eslint-disable-next-line complexity
export function getRecommendedDevice(
  data: QuizDataInput | null,
  defaultProduct: ProductKey,
): ProductKey {
  if (!data) {
    return defaultProduct
  }

  const {
    eligibleAppleWatch,
    purchaseNewWatch,
    whichWearable,
    wearWatchToBed,
    sleepWithTracker,
    troubleGettingBackToSleep,
    sleepConsistency,
    deviceTrait,
  } = data

  /* ---PRIO: 1--- */

  /* Recommend Oura:
  - if user owns Oura or both Apple and Oura */
  if (whichWearable?.includes(WhichWearableBreakpointOption.ouraRing)) {
    return ProductKey.OURA_RING_DISCOUNT
  }

  /* Recommend Apple Watch:
  - if user owns eligible Apple Watch or is willing to buy one
  - AND if user sleeps with watches or both with ring and watch */
  if (
    (eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes ||
      purchaseNewWatch === PurchaseNewWatchOption.yes) &&
    (wearWatchToBed === WearWatchToBedOption.yes ||
      sleepWithTracker === SleepWithTrackerOption.yesWithBoth ||
      sleepWithTracker === SleepWithTrackerOption.yesWithWatch)
  ) {
    return ProductKey.APPLE_WATCH
  }

  /* Recommend Apple Watch:
- if user wears neither device or wears only ring
- BUT is willing to wear wear watch to bed */
  if (
    (sleepWithTracker === SleepWithTrackerOption.neither ||
      sleepWithTracker === SleepWithTrackerOption.yesWithRing) &&
    wearWatchToBed === WearWatchToBedOption.yes
  ) {
    return ProductKey.APPLE_WATCH
  }

  /* ---PRIO: 2--- */

  /* Recommend Thermometer:
    - if user doesn't own any devices and is cost-conscious */
  /* Recommend Oura:
    - if user doesn't own any devices and wants premium experience */
  if (
    whichWearable?.length === 0 ||
    // if users are not willing to buy an elligable watch they will be treated as if they don't own any devices
    (whichWearable?.includes(WhichWearableBreakpointOption.appleWatch) &&
      purchaseNewWatch === PurchaseNewWatchOption.no)
  ) {
    if (deviceTrait === DeviceTraitOption.costConscious) {
      return defaultProduct
    }
    if (deviceTrait === DeviceTraitOption.premium) {
      return ProductKey.OURA_RING_DISCOUNT
    }
    /* Recommend Oura:
    - if user doesn't know what device trait they value but they have answered one of the oura answers
    1. owns Oura ring, //checked in PRIO 1
    2. sometimes struggles with sleep, or is awake often,
    3. sleep patters fluctuate, irregular sleep */
    if (
      deviceTrait === DeviceTraitOption.notSure &&
      (troubleGettingBackToSleep === TroubleGettingBackToSleepOption.someStruggle ||
        troubleGettingBackToSleep === TroubleGettingBackToSleepOption.oftenAwakeAWhile ||
        sleepConsistency === SleepConsistencyOption.patternsFluctuate ||
        sleepConsistency === SleepConsistencyOption.irregularSleep)
    ) {
      return ProductKey.OURA_RING_DISCOUNT
    }
  }

  /* ---PRIO: 3--- */

  /* Recommend Oura:
  - if user wears neither device or wears only ring AND doesnt know if they are willing to wear watch to bed
  -- if any of the following Oura conditions are met --
  1. owns Oura ring, //checked in PRIO 1
  2. sometimes struggles with sleep, or is awake often,
  3. sleep patters fluctuate, irregular sleep,
  4. premium experience
  -- otherwise recommend Thermometer */

  if (deviceTrait === DeviceTraitOption.premium) {
    return ProductKey.OURA_RING_DISCOUNT
  }

  if (
    (sleepWithTracker === SleepWithTrackerOption.neither ||
      sleepWithTracker === SleepWithTrackerOption.yesWithRing) &&
    (wearWatchToBed === WearWatchToBedOption.no || wearWatchToBed === WearWatchToBedOption.dontKnow)
  ) {
    if (
      troubleGettingBackToSleep === TroubleGettingBackToSleepOption.someStruggle ||
      troubleGettingBackToSleep === TroubleGettingBackToSleepOption.oftenAwakeAWhile ||
      sleepConsistency === SleepConsistencyOption.patternsFluctuate ||
      sleepConsistency === SleepConsistencyOption.irregularSleep
    ) {
      return ProductKey.OURA_RING_DISCOUNT
    }
    return defaultProduct
  }

  // if apple watch exists in whichWearable, we still need to make sure Oura overrides it if they are not willing purchase a new watch or wear watch to bed
  if (
    whichWearable?.includes(WhichWearableBreakpointOption.appleWatch) &&
    (purchaseNewWatch === PurchaseNewWatchOption.no ||
      wearWatchToBed === WearWatchToBedOption.no) &&
    (troubleGettingBackToSleep === TroubleGettingBackToSleepOption.someStruggle ||
      troubleGettingBackToSleep === TroubleGettingBackToSleepOption.oftenAwakeAWhile ||
      sleepConsistency === SleepConsistencyOption.patternsFluctuate ||
      sleepConsistency === SleepConsistencyOption.irregularSleep)
  ) {
    return ProductKey.OURA_RING_DISCOUNT
  }

  return defaultProduct
}

export function getRecommendedDeviceTranslationKey(bestDeviceKey: ProductKey): string {
  return bestDeviceKey === ProductKey.APPLE_WATCH
    ? `product--${bestDeviceKey}--title`
    : `measuring-hw-name--${bestDeviceKey}`
}

export function getDeviceInfo(device: ProductKey): DeviceInfo {
  const deviceText = 'txt-best-device-info'

  switch (device) {
    case ProductKey.APPLE_WATCH: {
      return {
        imgUrl: 'assets/img/quiz-result/apple-watch.png',
        title: 'product--APPLE_WATCH--title',
        text: deviceText,
      }
    }
    case ProductKey.OURA_RING_DISCOUNT: {
      return {
        imgUrl: 'assets/img/quiz-result/oura-ring.png',
        title: 'product--OURA_RING--title',
        text: deviceText,
      }
    }
  }
  return {
    imgUrl: 'assets/img/quiz-result/thermometer.png',
    title: 'cart-THERMOMETER',
    text: deviceText,
  }
}

export function getDeviceImgUrl(device: ProductKey): string {
  switch (device) {
    case ProductKey.APPLE_WATCH: {
      return '/assets/img/ab-264/apple-watch.png'
    }
    case ProductKey.OURA_RING_DISCOUNT: {
      return '/assets/img/ab-264/oura.png'
    }
  }
  return '/assets/img/ab-264/thermometer.png'
}

export function getGoalImageUrl(goal?: GoalBreakpointOption): string {
  return goal === GoalBreakpointOption.plan
    ? '/assets/img/ab-264/plan.png'
    : '/assets/img/ab-264/prevent.png'
}

export function getFirstKeyPoint(
  hormoneUsage?: HormonalBirthControlHowLongBreakpointOption,
  goal?: GoalBreakpointOption,
  birthControlMethod?: BirthControlMethodBreakpointOption,
): KeyPoint {
  switch (goal) {
    case GoalBreakpointOption.plan: {
      return {
        text: 'plan-naturally',
        imgUrl: '/assets/img/quiz-result/plan-pregnancy.svg',
      }
    }
  }
  if (birthControlMethod && isNonHormonalBirthControl(birthControlMethod)) {
    return {
      text: 'no-need-hormones',
      imgUrl: '/assets/img/quiz-result/say-goodbye.svg',
    }
  }

  switch (hormoneUsage) {
    case HormonalBirthControlHowLongBreakpointOption.moreThanAYear: {
      return {
        text: 'goodbye-hormones-finally',
        imgUrl: '/assets/img/quiz-result/say-goodbye.svg',
      }
    }
    case HormonalBirthControlHowLongBreakpointOption.fewMonths:
    case HormonalBirthControlHowLongBreakpointOption.lessThanAMonth: {
      return {
        text: 'goodbye-hormones',
        imgUrl: '/assets/img/quiz-result/say-goodbye.svg',
      }
    }
  }
  return {
    text: 'no-need-hormones',
    imgUrl: '/assets/img/quiz-result/say-goodbye.svg',
  }
}

export function getSecondKeyPoint(periodRegularity?: PeriodRegularityOption): KeyPoint {
  switch (periodRegularity) {
    case PeriodRegularityOption.canUsuallyPredict: {
      return {
        text: 'harness-the-power',
        imgUrl: '/assets/img/quiz-result/take-control.svg',
      }
    }
    case PeriodRegularityOption.hardToPredict: {
      return {
        text: 'understand-irregularities',
        imgUrl: '/assets/img/quiz-result/take-control.svg',
      }
    }
  }
  return { text: 'take-control', imgUrl: '/assets/img/quiz-result/take-control.svg' }
}

export function isNonHormonalBirthControl(bc: BirthControlMethodBreakpointOption): boolean {
  const nonHormonalMethods = [
    BirthControlMethodBreakpointOption.copperIUD,
    BirthControlMethodBreakpointOption.condom,
    BirthControlMethodBreakpointOption.pullOut,
    BirthControlMethodBreakpointOption.other,
    BirthControlMethodBreakpointOption.none,
  ]

  return nonHormonalMethods.includes(bc)
}

export function isHormonalBirthControl(bc: BirthControlMethodBreakpointOption): boolean {
  const hormonalMethods = [
    BirthControlMethodBreakpointOption.hormonalIUD,
    BirthControlMethodBreakpointOption.implant,
    BirthControlMethodBreakpointOption.nuvaRing,
    BirthControlMethodBreakpointOption.patch,
    BirthControlMethodBreakpointOption.pill,
    BirthControlMethodBreakpointOption.shot,
  ]
  return hormonalMethods.includes(bc)
}

export function getThirdKeyPoint(conditions?: MedicalConditionsBreakpointOption[]): KeyPoint {
  if (!conditions?.length) {
    return {
      text: 'gain-insight',
      imgUrl: '/assets/img/quiz-result/gain-insights.svg',
    }
  }
  if (conditions.length > 1) {
    return {
      text: 'track-symptoms-conditions',
      imgUrl: '/assets/img/quiz-result/gain-insights.svg',
    }
  }
  switch (conditions[0]) {
    case MedicalConditionsBreakpointOption.endometriosis: {
      return {
        text: 'track-symptoms-endometriosis',
        imgUrl: '/assets/img/quiz-result/gain-insights.svg',
      }
    }
    case MedicalConditionsBreakpointOption.pcos: {
      return {
        text: 'track-symptoms-PCOS',
        imgUrl: '/assets/img/quiz-result/gain-insights.svg',
      }
    }
    case MedicalConditionsBreakpointOption.thyroid: {
      return {
        text: 'track-symptoms-condition',
        imgUrl: '/assets/img/quiz-result/gain-insights.svg',
      }
    }
  }

  return {
    text: 'gain-insight',
    imgUrl: '/assets/img/quiz-result/gain-insights.svg',
  }
}

export const getFlowForDefinition = (
  flowDefinition: FlowDefinition,
  quizService: QuizService,
  quizData?: QuizDataInput,
): SignupQuizFlow => {
  let flow = commonQuizFlow
  for (const [breakpoint, choice] of Object.entries(flowDefinition) as [
    Breakpoint,
    BreakpointOption | BreakpointOption[],
  ][]) {
    if (Array.isArray(choice)) {
      choice.forEach((c: BreakpointOption) => {
        flow = quizService.getEngine().processBreakpointAction(breakpoint, c, flow, quizData)
      })
    } else {
      flow = quizService.getEngine().processBreakpointAction(breakpoint, choice, flow, quizData)
    }
  }
  return flow
}

export function addPageInSectionAtIndex(
  flow: Page[][],
  pageToAdd: Page,
  section: DefaultSections,
  index: number,
): Page[][] {
  const sectionPages = flow[allQuizSections[section]] || []
  const alreadyExists = sectionPages.includes(pageToAdd)
  if (alreadyExists) {
    return flow
  }
  sectionPages.splice(index, 0, pageToAdd)

  flow[allQuizSections[section]] = sectionPages
  return flow
}

export function addPageAtEndOfSection(
  flow: Page[][],
  pageToAdd: Page,
  section: DefaultSections,
): Page[][] {
  const sectionPages = flow[allQuizSections[section]] || []
  const alreadyExists = sectionPages.includes(pageToAdd)
  if (alreadyExists) {
    return flow
  }
  sectionPages.push(pageToAdd)

  flow[allQuizSections[section]] = sectionPages
  return flow
}

export function addPageInSectionAfterPage(
  flow: Page[][],
  pageToAdd: Page,
  pageAfter: Page,
  section: DefaultSections,
): Page[][] {
  const sectionPages = flow[allQuizSections[section]] || []
  const alreadyExists = sectionPages.includes(pageToAdd)
  if (alreadyExists) {
    return flow
  }
  const index = sectionPages.indexOf(pageAfter)
  // It's on the caller to check if the pageAfter exists before calling this function
  if (index === -1) {
    return flow
  }

  return addPageInSectionAtIndex(flow, pageToAdd, section, index + 1)
}

export function addPageAtStartOfSection(
  flow: Page[][],
  pageToAdd: Page,
  section: DefaultSections,
): Page[][] {
  const sectionPages = flow[allQuizSections[section]] || []
  const alreadyExists = sectionPages.includes(pageToAdd)
  if (alreadyExists) {
    return flow
  }
  sectionPages.unshift(pageToAdd)

  flow[allQuizSections[section]] = sectionPages
  return flow
}

export function filterPagesFromSection(
  flow: Page[][],
  pagesToRemove: Page[],
  section: DefaultSections,
): Page[][] {
  const sectionPages = flow[allQuizSections[section]] || []
  flow[allQuizSections[section]] = sectionPages.filter(p => !pagesToRemove.includes(p))
  return flow
}
